import { Button, DatePicker, Dropdown, DropdownProps, Menu, Radio, Space, Typography } from "antd";
import dayjs from "dayjs";
import {  useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import { FaCalendarAlt } from "react-icons/fa";

const { RangePicker } = DatePicker;

const dateOptions = [
    {
        key: "Today",
        label: "Today",
        startTime: dayjs().startOf("day").toISOString(),
        endTime: dayjs().endOf("day").toISOString()
    },
    {
        key: "Yesterday",
        label: "Yesterday",
        startTime: dayjs().subtract(1, "day").startOf("day").toISOString(),
        endTime: dayjs().subtract(1, "day").endOf("day").toISOString()
    },
    {
        key: "Week to Date",
        label: "Week to Date",
        startTime: dayjs().startOf("week").toISOString(),
        endTime: dayjs().endOf("day").toISOString()
    },
    {
        key: "Last Week",
        label: "Last Week",
        startTime: dayjs().subtract(1, "week").startOf("week").toISOString(),
        endTime: dayjs().subtract(1, "week").endOf("week").toISOString()
    },
    {
        key: "Last 7 Days",
        label: "Last 7 Days",
        startTime: dayjs().subtract(7, "day").startOf("day").toISOString(),
        endTime: dayjs().subtract(1, "day").endOf("day").toISOString()
    },
    {
        key: "Month to Date",
        label: "Month to Date",
        startTime: dayjs().startOf("month").toISOString(),
        endTime: dayjs().endOf("day").toISOString()
    },
    {
        key: "Last Month",
        label: "Last Month",
        startTime: dayjs().subtract(1, "month").startOf("month").toISOString(),
        endTime: dayjs().subtract(1, "month").endOf("month").toISOString()
    },
    {
        key: "Last 30 Days",
        label: "Last 30 Days",
        startTime: dayjs().subtract(30, "day").startOf("day").toISOString(),
        endTime: dayjs().subtract(1, "day").endOf("day").toISOString()
    },
    {
        key: "Last 90 Days",
        label: "Last 90 Days",
        startTime: dayjs().subtract(90, "day").startOf("day").toISOString(),
        endTime: dayjs().endOf("day").toISOString()
    },
    {
        key: "Year to Date",
        label: "Year to Date",
        startTime: dayjs().startOf("year").toISOString(),
        endTime: dayjs().endOf("day").toISOString()
    },
    {
        key: "Last Year",
        label: "Last Year",
        startTime: dayjs().subtract(1, "year").startOf("year").toISOString(),
        endTime: dayjs().subtract(1, "year").endOf("year").toISOString()
    }
];

function getDateOptionByRange(start_date: Date, end_date: Date) {
    return dateOptions.find(option => dayjs(option.startTime).isSame(dayjs(start_date)) && dayjs(option.endTime).isSame(dayjs(end_date)));
}
const menuItems = dateOptions.map(option => ({
    key: option.key,
    label: option.label
}));

export const DateSelector = ({
    date,
    setDate
}: {
    date: {
        start_date: Date;
        end_date: Date;
    } ;
    setDate: (date: { start_date: Date; end_date: Date } ) => void;
}) => {
    const { start_date, end_date } = date || {};
    const [selectedDateOption, setSelectedDateOption] = useState<string | null>((start_date && end_date && getDateOptionByRange(start_date, end_date)?.key) || null);
    const [customDateRange, setCustomDateRange] = useState<any[]>(start_date && end_date && !getDateOptionByRange(start_date, end_date) ? [dayjs(start_date), dayjs(end_date)] : []);
    const [openDateOptionModal, setOpenDateOptionModal] = useState<boolean>(false);



    const menu = (
        <Menu
            items={[
                ...menuItems.map(item => ({
                    key: item.key,
                    label: (
                        <Radio.Group style={{ width: "100%" }} value={selectedDateOption} onChange={e => setSelectedDateOption(e.target.value)}>
                            <Radio
                                style={{ display: "flex", flexDirection: "row-reverse", justifyContent: "space-between", width: "100%" }}
                                onChange={e => {
                                    setSelectedDateOption(e.target.value);
                                    setCustomDateRange([]);
                                    const selectedOption = dateOptions.find(option => option.key === e.target.value);
                                    if (selectedOption) {
                                        setDate({
                                            start_date: new Date(selectedOption.startTime),
                                            end_date: new Date(selectedOption.endTime)
                                        });
                                    }
                                }}
                                value={item.key}
                            >
                                <Typography.Text strong style={{ width: "100%" }}>
                                    {item.label}
                                </Typography.Text>
                            </Radio>
                        </Radio.Group>
                    )
                })),
                {
                    key: "custom",
                    label: (
                        <RangePicker
                            style={{ background: "#EFEDFF" }}
                            format={"MM/DD/YYYY"}
                            disabledDate={current => current && current > dayjs().add(1, "day").endOf("day")}
                            placeholder={["Start Date", "End Date"]}
                            allowClear
                            defaultValue={undefined}
                            onChange={value => {
                                if (value && value[0] && value[1]) {
                                    const startDate = value[0].startOf("day").toISOString();
                                    const endDate = value[1].endOf("day").toISOString();
                                    setDate({ start_date: new Date(startDate), end_date: new Date(endDate) });
                                    setCustomDateRange([startDate, endDate]);
                                    const selectedOption = getDateOptionByRange(new Date(startDate), new Date(endDate));
                                    if (selectedOption) {
                                        setSelectedDateOption(selectedOption.key);
                                    } else {
                                        setSelectedDateOption(null);
                                    }
                                } else {
                                    setCustomDateRange([]);
                                }
                            }}
                            value={customDateRange && customDateRange.length > 0 ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])] : undefined}
                        />
                    )
                },
                {
                    key: "clear",
                    label: (
                        <Button
                            style={{ width: "fit-content" }}
                            type="link"
                            onClick={() => {
                                setSelectedDateOption(null);
                                setCustomDateRange([]);
                                setDate({ start_date: new Date(dayjs().startOf("day").toISOString()), end_date: new Date(dayjs().endOf("day").toISOString()) });
                            }}
                        >
                            Clear
                        </Button>
                    )
                }
            ]}
        />
    );

    const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
        if (info.source === "trigger" || nextOpen) {
            setOpenDateOptionModal(nextOpen);
        }
    };

    return (
        <Dropdown className="date-dropdown-container" open={openDateOptionModal} overlay={menu} trigger={["click"]} onOpenChange={handleOpenChange}>
            <Button
                type="primary"
                onClick={e => {
                    e.preventDefault();
                    setOpenDateOptionModal(true);
                }}
            >
                <Space>
                    <FaCalendarAlt style={{ marginTop: "4px" }} />
                    {customDateRange.length > 1 ? (
                        <Typography.Text style={{ color: "white" }} strong>
                            {dayjs(start_date).format("MM/DD/YYYY")} - {dayjs(end_date).format("MM/DD/YYYY")}
                        </Typography.Text>
                    ) : selectedDateOption ? (
                        <Typography.Text style={{ color: "white" }} strong>
                            {selectedDateOption}
                        </Typography.Text>
                    ) : (
                        <Typography.Text style={{ color: "white" }} strong>
                            Select Date
                        </Typography.Text>
                    )}
                    <FaCaretDown style={{ marginTop: "4px" }} />
                </Space>
            </Button>
        </Dropdown>
    );
};
